import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { qrAPI, analyticsAPI } from '../services/api';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ReactECharts from 'echarts-for-react';
const { FiQrCode, FiBarChart3, FiUsers, FiTrendingUp, FiPlus, FiEye, FiDownload, FiMoreVertical } = FiIcons;

const Dashboard = () => {
  const { currentWorkspace } = useWorkspace();
  const [qrCodes, setQrCodes] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentWorkspace) {
      fetchDashboardData();
    }
  }, [currentWorkspace]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [qrData, analyticsData] = await Promise.all([
        qrAPI.getAll(currentWorkspace.id),
        analyticsAPI.getWorkspaceAnalytics(currentWorkspace.id)
      ]);
      setQrCodes(qrData.slice(0, 5)); // Show only 5 recent QR codes
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    { name: 'Total QR Codes', value: analytics?.totalQRCodes || 0, icon: FiQrCode, color: 'bg-blue-500', change: '+12%', changeType: 'increase' },
    { name: 'Total Scans', value: analytics?.totalScans || 0, icon: FiEye, color: 'bg-green-500', change: '+18%', changeType: 'increase' },
    { name: 'Active Campaigns', value: analytics?.activeCampaigns || 0, icon: FiTrendingUp, color: 'bg-purple-500', change: '+5%', changeType: 'increase' },
    { name: 'Team Members', value: analytics?.teamMembers || 0, icon: FiUsers, color: 'bg-orange-500', change: '+2', changeType: 'increase' }
  ];

  const chartOptions = {
    title: {
      text: 'Scan Analytics (Last 7 Days)',
      left: 'center'
    },
    tooltip: {
      trigger: 'axis'
    },
    xAxis: {
      type: 'category',
      data: analytics?.chartData?.dates || []
    },
    yAxis: {
      type: 'value'
    },
    series: [
      {
        data: analytics?.chartData?.scans || [],
        type: 'line',
        smooth: true,
        itemStyle: {
          color: '#3b82f6'
        }
      }
    ]
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Welcome back! Here's what's happening with your QR codes.
          </p>
        </div>
        <Link
          to="/generate"
          className="mt-3 sm:mt-0 inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
          Create QR Code
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
          >
            <div className="flex items-center">
              <div className={`p-3 rounded-lg ${stat.color}`}>
                <SafeIcon icon={stat.icon} className="w-6 h-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-2xl font-semibold text-gray-900">{stat.value.toLocaleString()}</p>
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <span className={`font-medium ${stat.changeType === 'increase' ? 'text-green-600' : 'text-red-600'}`}>
                {stat.change}
              </span>
              <span className="text-gray-500 ml-1">from last month</span>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Charts and Recent QR Codes */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-gray-200"
        >
          <ReactECharts option={chartOptions} style={{ height: '300px' }} />
        </motion.div>

        {/* Recent QR Codes */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recent QR Codes</h3>
            <Link to="/generate" className="text-blue-600 hover:text-blue-700 text-sm font-medium">
              View All
            </Link>
          </div>
          <div className="space-y-4">
            {qrCodes.length > 0 ? (
              qrCodes.map((qr) => (
                <div key={qr.id} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <SafeIcon icon={FiQrCode} className="w-5 h-5 text-gray-600" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">{qr.title}</p>
                      <p className="text-xs text-gray-500">{qr.type}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">{qr.scans || 0} scans</span>
                    <button className="p-1 hover:bg-gray-100 rounded">
                      <SafeIcon icon={FiMoreVertical} className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <SafeIcon icon={FiQrCode} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No QR codes yet</p>
                <Link to="/generate" className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                  Create your first QR code
                </Link>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
        className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link
            to="/generate"
            className="flex items-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors group"
          >
            <SafeIcon icon={FiQrCode} className="w-8 h-8 text-gray-400 group-hover:text-blue-500" />
            <div className="ml-3">
              <p className="font-medium text-gray-900">Generate QR</p>
              <p className="text-sm text-gray-500">Create new QR code</p>
            </div>
          </Link>
          <Link
            to="/analytics"
            className="flex items-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-400 hover:bg-green-50 transition-colors group"
          >
            <SafeIcon icon={FiBarChart3} className="w-8 h-8 text-gray-400 group-hover:text-green-500" />
            <div className="ml-3">
              <p className="font-medium text-gray-900">View Analytics</p>
              <p className="text-sm text-gray-500">Track performance</p>
            </div>
          </Link>
          <Link
            to="/team"
            className="flex items-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors group"
          >
            <SafeIcon icon={FiUsers} className="w-8 h-8 text-gray-400 group-hover:text-purple-500" />
            <div className="ml-3">
              <p className="font-medium text-gray-900">Manage Team</p>
              <p className="text-sm text-gray-500">Add team members</p>
            </div>
          </Link>
          <Link
            to="/domains"
            className="flex items-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-400 hover:bg-orange-50 transition-colors group"
          >
            <SafeIcon icon={FiTrendingUp} className="w-8 h-8 text-gray-400 group-hover:text-orange-500" />
            <div className="ml-3">
              <p className="font-medium text-gray-900">Custom Domain</p>
              <p className="text-sm text-gray-500">Brand your links</p>
            </div>
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default Dashboard;